/**
 * LOT-UI-03b.2 — Carte vente mobile (Historique, DS Remobiles).
 */
import React from 'react';
import { User, Banknote, CreditCard, Calendar } from 'lucide-react';
import MagasinBadge from '../MagasinBadge';
import HistoriqueVenteActionsMenu from './HistoriqueVenteActionsMenu';

function typeBadgeClass(type) {
  if (type === 'retrait') return 'rm-badge rm-badge--danger';
  if (type === 'acompte' || type === 'complement') return 'rm-badge rm-badge--muted';
  return 'rm-badge rm-badge--success';
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function HistoriqueVenteCard({
  vente,
  isAdmin,
  convertingRef,
  actionHandlers,
  testIdPrefix = 'historique',
}) {
  const typeLabel = vente.type === 'complement' ? 'complément' : (vente.type || 'vente');

  return (
    <article
      className="rounded-xl border p-4 space-y-3"
      style={{
        background: 'var(--rm-bg-elevated)',
        borderColor: 'var(--rm-border-subtle)',
      }}
      data-testid={`${testIdPrefix}-card-${vente.ref_vente}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p
            className="font-mono text-sm font-bold truncate"
            style={{ color: 'var(--rm-white)' }}
            data-testid={`${testIdPrefix}-card-ref-${vente.ref_vente}`}
          >
            {vente.ref_vente}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <MagasinBadge code={vente.magasin_id || 'M1'} size="sm" />
            <span className={typeBadgeClass(vente.type)}>{typeLabel}</span>
            {vente.statut_paiement === 'partielle' && (
              <span className="rm-badge rm-badge--danger">
                Reste {vente.reste_a_payer?.toFixed(2)} €
              </span>
            )}
          </div>
        </div>
        <HistoriqueVenteActionsMenu
          vente={vente}
          isAdmin={isAdmin}
          convertingRef={convertingRef}
          testIdPrefix={testIdPrefix}
          {...actionHandlers}
        />
      </div>

      <p className="text-sm line-clamp-2" style={{ color: 'var(--rm-gray-100)' }}>
        {vente.designation}
      </p>

      <div className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg" style={{ background: 'var(--rm-bg-panel)' }}>
        <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--rm-gray-500)' }}>Montant</span>
        <span className="text-lg font-bold" style={{ color: 'var(--rm-success)' }}>
          {vente.prix_total?.toFixed(2)} €
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex items-center gap-2" style={{ color: 'var(--rm-gray-400)' }}>
          {vente.mode_paiement?.includes('Espèces') && <Banknote className="w-4 h-4 text-[var(--rm-success)]" />}
          {(vente.mode_paiement?.includes('CB') || vente.mode_paiement?.includes('Carte')) && (
            <CreditCard className="w-4 h-4 text-[var(--rm-gray-400)]" />
          )}
          <span>{vente.mode_paiement}</span>
        </div>
        <div className="flex items-center gap-2" style={{ color: 'var(--rm-gray-400)' }}>
          <User className="w-4 h-4" />
          <span>{vente.vendeur}</span>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--rm-gray-500)' }}>
          <Calendar className="w-3.5 h-3.5" />
          <span>{formatDate(vente.date_vente)}</span>
        </div>
      </div>
    </article>
  );
}
